/**
 * CloudflareStreamVideoController
 *
 * Extends MultiVideoController to support Cloudflare Stream HLS delivery
 * with adaptive bitrate streaming for optimized video loading.
 */

import {
  MultiVideoController,
  VideoLoadError,
  VideoNotFoundError,
  VideoNotLoadedError,
} from './multi-video-controller.js';
import * as THREE from 'three';
import Hls from 'hls.js';

/**
 * StreamVideoAsset extends VideoAsset with Cloudflare Stream properties
 */
class StreamVideoAsset {
  constructor(markerId, videoPath, streamConfig = null) {
    this.videoId = markerId;
    this.videoPath = videoPath; // Fallback MP4 path
    this.videoElement = null;
    this.texture = null;
    this.loadStatus = 'pending'; // pending, loading, loaded, failed
    this.loadProgress = 0;
    this.playbackStatus = 'stopped'; // stopped, playing, paused, buffering
    this.fileSize = null;
    this.isCached = false;
    this.lastError = null;

    // Cloudflare Stream properties
    this.deliveryType = streamConfig ? 'cloudflare-stream' : 'direct-mp4';
    this.streamVideoId = streamConfig?.streamVideoId || null;
    this.streamManifestUrl = streamConfig?.manifestUrl || null;
    this.hlsInstance = null;
    this.currentQualityLevel = null;

    // T026-1: Buffer monitoring
    this.firstBufferLogged = false;
  }
}

/**
 * CloudflareStreamVideoController class
 */
export class CloudflareStreamVideoController extends MultiVideoController {
  constructor(config = {}) {
    super();

    // Cloudflare Stream configuration
    this.cloudflareAccountId = config.accountId || null;
    this.cloudflareCustomerSubdomain = config.customerSubdomain || null;
    this.hlsConfig = {
      enableWorker: true,
      lowLatencyMode: false,
      backBufferLength: 90,
      maxBufferLength: 30,
      maxMaxBufferLength: 60,
      ...config.hlsConfig,
    };
  }

  /**
   * Build Cloudflare Stream HLS manifest URL
   * @param {string} streamVideoId - Cloudflare Stream video ID
   * @returns {string} HLS manifest URL
   * @private
   */
  _buildStreamManifestUrl(streamVideoId) {
    if (this.cloudflareCustomerSubdomain) {
      return `https://${this.cloudflareCustomerSubdomain}.cloudflarestream.com/${streamVideoId}/manifest/video.m3u8`;
    }
    return `https://cloudflarestream.com/${streamVideoId}/manifest/video.m3u8`;
  }

  /**
   * Preload all marker videos (override with Stream support)
   * @param {Array} markers - Array of MarkerDefinition objects with optional streamConfig
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<void>}
   */
  async preloadAll(markers, onProgress = null) {
    if (this.isPreloading) {
      console.warn('Preload already in progress');
      return;
    }

    this.isPreloading = true;
    const totalMarkers = markers.length;
    let loadedCount = 0;

    console.log(`🎬 Preloading ${totalMarkers} videos (Cloudflare Stream enabled)...`);

    // Create StreamVideoAssets for all markers
    markers.forEach((marker) => {
      const asset = new StreamVideoAsset(marker.id, marker.videoPath, marker.streamConfig);
      this.videos.set(marker.id, asset);
    });

    // Load all videos in parallel
    const preloadPromises = markers.map(async (marker) => {
      const asset = this.videos.get(marker.id);

      try {
        await this._loadStreamVideoAsset(asset, (percent) => {
          asset.loadProgress = percent;

          // Calculate overall progress
          if (onProgress) {
            let totalProgress = 0;
            this.videos.forEach((v) => {
              totalProgress += v.loadProgress;
            });
            const overallPercent = totalProgress / totalMarkers;
            onProgress(overallPercent);
          }
        });

        asset.loadStatus = 'loaded';
        loadedCount++;
        console.log(
          `✅ Video loaded: ${marker.id} (${loadedCount}/${totalMarkers}) [${asset.deliveryType}]`
        );
      } catch (error) {
        asset.loadStatus = 'failed';
        asset.lastError = error;
        console.error(`❌ Failed to load video ${marker.id}:`, error);
      }
    });

    await Promise.all(preloadPromises);

    this.isPreloading = false;
    this.preloadProgress = 100;

    const successCount = Array.from(this.videos.values()).filter(
      (v) => v.loadStatus === 'loaded'
    ).length;

    console.log(`✅ Preload complete: ${successCount}/${totalMarkers} videos loaded`);

    if (successCount === 0) {
      throw new VideoLoadError('all', new Error('Failed to load any videos'));
    }
  }

  /**
   * Load a single video asset with Cloudflare Stream HLS support
   * @param {StreamVideoAsset} asset - StreamVideoAsset to load
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<void>}
   * @private
   */
  async _loadStreamVideoAsset(asset, onProgress = null) {
    asset.loadStatus = 'loading';

    try {
      // Create video element
      const videoElement = document.createElement('video');
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('webkit-playsinline', '');
      videoElement.setAttribute('crossorigin', 'anonymous');
      videoElement.setAttribute('preload', 'auto');
      videoElement.loop = false; // Manual loop control for better control
      videoElement.muted = true; // Required for autoplay

      // Add loop restart handler (conditional - only when actively playing)
      videoElement.addEventListener('ended', () => {
        // Only restart if the video is supposed to be playing
        // This prevents unwanted playback when:
        // - Marker is not visible (playbackStatus = 'stopped' or 'paused')
        // - Browser is in background
        if (asset.playbackStatus === 'playing') {
          console.log(`🔄 Video ended: ${asset.videoId}, restarting from beginning`);
          videoElement.currentTime = 0;
          videoElement.play().catch((error) => {
            console.error(`Failed to restart video ${asset.videoId}:`, error);
          });
        } else {
          console.log(
            `⏸️ Video ended: ${asset.videoId}, not restarting (status: ${asset.playbackStatus})`
          );
        }
      });

      // T026-1: Monitor buffering state (FR-020)
      videoElement.addEventListener('waiting', () => {
        // REMOVED: Frequent logs cause iOS performance issues
        // console.warn(`⏳ [BUFFERING] ${asset.videoId}: Video is buffering (insufficient data)`);
        asset.playbackStatus = 'buffering';
      });

      videoElement.addEventListener('canplay', () => {
        // REMOVED: Frequent logs cause iOS performance issues
        // console.log(`✅ [BUFFERING] ${asset.videoId}: Sufficient buffer available`);
        if (asset.playbackStatus === 'buffering') {
          asset.playbackStatus = 'playing';
        }
      });

      // T026-1: Buffer monitoring disabled (causes iOS performance issues)
      // videoElement.addEventListener('progress', () => {
      //   if (videoElement.buffered.length > 0) {
      //     const bufferedEnd = videoElement.buffered.end(videoElement.buffered.length - 1);
      //     const bufferedSeconds = bufferedEnd - videoElement.currentTime;
      //     if (bufferedSeconds < 5 || !asset.firstBufferLogged) {
      //       console.log(`📊 [BUFFER] ${asset.videoId}: ${bufferedSeconds.toFixed(1)}s buffered`);
      //       asset.firstBufferLogged = true;
      //     }
      //   }
      // });

      // Check if HLS streaming is available
      if (asset.deliveryType === 'cloudflare-stream' && asset.streamVideoId) {
        console.log(`📺 Loading HLS stream: ${asset.videoId}`);

        // Build manifest URL if not provided
        if (!asset.streamManifestUrl) {
          asset.streamManifestUrl = this._buildStreamManifestUrl(asset.streamVideoId);
        }

        // iOS Safari has native HLS support
        if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
          console.log(`🍎 Using native HLS for ${asset.videoId}`);
          videoElement.src = asset.streamManifestUrl;

          // iOS用: 明示的にload()を呼んで読み込み開始
          videoElement.load();

          // Quality monitoring disabled (causes iOS performance issues)
          // if (this.hlsConfig.features?.enableQualityLogging !== false) {
          //   let lastHeight = 0;
          //   videoElement.addEventListener('loadedmetadata', () => {
          //     const currentHeight = videoElement.videoHeight;
          //     if (currentHeight !== lastHeight && lastHeight !== 0) {
          //       console.log(`🔄 Quality changed to ~${currentHeight}p for ${asset.videoId} (native HLS)`);
          //     }
          //     lastHeight = currentHeight;
          //   });
          //   videoElement.addEventListener('progress', () => {
          //     const currentHeight = videoElement.videoHeight;
          //     if (currentHeight !== lastHeight && lastHeight !== 0) {
          //       console.log(`🔄 Quality changed to ~${currentHeight}p for ${asset.videoId} (native HLS)`);
          //     }
          //     lastHeight = currentHeight;
          //   });
          // }

          await new Promise((resolve, reject) => {
            // canplayイベント使用: 最小バッファで再生可能な時点で解決
            videoElement.addEventListener(
              'canplay',
              () => {
                console.log(`✅ iOS HLS video ready (canplay): ${asset.videoId}`);
                if (onProgress) onProgress(100);
                resolve();
              },
              { once: true }
            );

            videoElement.onerror = () => reject(new Error('Failed to load HLS stream'));
            setTimeout(() => reject(new Error('HLS load timeout')), 30000);
          });

          // iOS用: 再生エンジンの事前準備（play→pause）でタイムラグを削減
          // DISABLED: iOS動画揺れ問題のため無効化
          // タイムラグ削減よりも揺れ解消を優先
          // try {
          //   await videoElement.play();
          //   await new Promise(resolve => setTimeout(resolve, 100));
          //   videoElement.pause();
          //   console.log(`🎬 Video playback engine prepared: ${asset.videoId}`);
          // } catch (error) {
          //   console.warn(`⚠️ Failed to prepare playback: ${error.message}`);
          // }
        }
        // Use HLS.js for other browsers
        else if (Hls.isSupported()) {
          console.log(`📡 Using HLS.js for ${asset.videoId}`);
          const hls = new Hls(this.hlsConfig);

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log(`✅ HLS manifest parsed for ${asset.videoId}`);
            console.log(
              `📊 Available quality levels: ${hls.levels.map((l) => `${l.height}p`).join(', ')}`
            );
            if (onProgress) onProgress(100);
          });

          hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
            asset.currentQualityLevel = hls.levels[data.level];
            // REMOVED: Frequent quality switch logs cause iOS performance issues
            // console.log(`🔄 Quality switched to ${asset.currentQualityLevel.height}p for ${asset.videoId}`);
          });

          hls.on(Hls.Events.ERROR, (event, data) => {
            if (data.fatal) {
              console.error(`❌ Fatal HLS error for ${asset.videoId}:`, data);
              hls.destroy();
              throw new Error(`HLS fatal error: ${data.type} - ${data.details}`);
            }
          });

          hls.loadSource(asset.streamManifestUrl);
          hls.attachMedia(videoElement);
          asset.hlsInstance = hls;

          // Wait for manifest to be parsed
          await new Promise((resolve, reject) => {
            hls.on(Hls.Events.MANIFEST_PARSED, resolve);
            hls.on(Hls.Events.ERROR, (event, data) => {
              if (data.fatal) reject(new Error(`HLS error: ${data.details}`));
            });
            setTimeout(() => reject(new Error('HLS manifest parse timeout')), 30000);
          });
        }
        // Fallback to direct MP4
        else {
          console.warn(`⚠️ HLS not supported, falling back to MP4 for ${asset.videoId}`);
          asset.deliveryType = 'direct-mp4-fallback';
          videoElement.src = asset.videoPath;

          await new Promise((resolve, reject) => {
            videoElement.onloadeddata = () => {
              if (onProgress) onProgress(100);
              resolve();
            };
            videoElement.onerror = () => reject(new Error('Failed to load fallback video'));
            setTimeout(() => reject(new Error('Fallback video load timeout')), 30000);
          });
        }
      }
      // Direct MP4 loading (original behavior)
      else {
        console.log(`📹 Loading direct MP4: ${asset.videoId}`);
        videoElement.src = asset.videoPath;

        await new Promise((resolve, reject) => {
          videoElement.onloadeddata = () => {
            if (onProgress) onProgress(100);
            resolve();
          };
          videoElement.onerror = () => reject(new Error('Failed to load video element'));
          setTimeout(() => reject(new Error('Video load timeout')), 30000);
        });
      }

      asset.videoElement = videoElement;

      // Create Three.js VideoTexture
      const texture = new THREE.VideoTexture(videoElement);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      asset.texture = texture;

      console.log(`🎬 Video element ready: ${asset.videoId} [${asset.deliveryType}]`);
    } catch (error) {
      throw new VideoLoadError(asset.videoPath, error);
    }
  }

  /**
   * Cleanup resources (override to cleanup HLS instances)
   */
  cleanup() {
    this.videos.forEach((asset) => {
      // Cleanup HLS instance
      if (asset.hlsInstance) {
        asset.hlsInstance.destroy();
        asset.hlsInstance = null;
      }

      // Cleanup video element
      if (asset.videoElement) {
        asset.videoElement.pause();
        asset.videoElement.src = '';
        asset.videoElement.load();
      }

      // Cleanup texture
      if (asset.texture) {
        asset.texture.dispose();
      }
    });

    this.videos.clear();
    this.currentMarkerId = null;
    this.isPreloading = false;
    this.preloadProgress = 0;

    console.log('CloudflareStreamVideoController cleaned up');
  }

  /**
   * Get HLS quality levels for a video
   * @param {string} markerId - Marker ID
   * @returns {Array|null} Array of quality levels or null
   */
  getQualityLevels(markerId) {
    const asset = this.videos.get(markerId);
    if (!asset || !asset.hlsInstance) {
      return null;
    }
    return asset.hlsInstance.levels;
  }

  /**
   * Get current quality level for a video
   * @param {string} markerId - Marker ID
   * @returns {Object|null} Current quality level or null
   */
  getCurrentQualityLevel(markerId) {
    const asset = this.videos.get(markerId);
    return asset?.currentQualityLevel || null;
  }

  /**
   * Set quality level for a video (manual override)
   * @param {string} markerId - Marker ID
   * @param {number} levelIndex - Quality level index (-1 for auto)
   */
  setQualityLevel(markerId, levelIndex) {
    const asset = this.videos.get(markerId);
    if (!asset || !asset.hlsInstance) {
      console.warn(`Cannot set quality level for ${markerId}: HLS not available`);
      return;
    }

    asset.hlsInstance.currentLevel = levelIndex;
    console.log(
      `🎚️ Quality level set to ${levelIndex === -1 ? 'auto' : levelIndex} for ${markerId}`
    );
  }

  /**
   * Mute/unmute all videos
   * @param {boolean} muted - True to mute
   */
  setMuted(muted) {
    console.log(`🔍 [CLOUDFLARE AUDIO] setMuted called with: ${muted}`);
    console.log(`🔍 [CLOUDFLARE AUDIO] Total videos: ${this.videos.size}`);

    let updatedCount = 0;
    this.videos.forEach((asset, markerId) => {
      if (asset.videoElement) {
        const beforeMuted = asset.videoElement.muted;
        asset.videoElement.muted = muted;
        console.log(`  ✓ ${markerId}: muted ${beforeMuted} → ${muted}`);
        updatedCount++;
      } else {
        console.warn(`  ⚠️ ${markerId}: No videoElement`);
      }
    });

    console.log(
      `🔊 [CLOUDFLARE AUDIO] Updated ${updatedCount}/${this.videos.size} videos to muted=${muted}`
    );
  }
}

// Export error classes for backward compatibility
export { VideoLoadError, VideoNotFoundError, VideoNotLoadedError };
